import { useCallback, useMemo } from 'react';
import { AdviseEngagement } from '../../../types/advise';
import { CallResult } from '../../../Api/callExternalApi';
import { deleteAdviseCurrentSpendOverride, updateAdviseCurrentSpendOverride } from '../../../Api/Advise/CurrentSpend';
import { fetchAdviseEngagementById } from '../../../Api/Advise/Engagements';
import { refusalMessage } from '../../../utils/refusalMessage';
import { EngagementCurrentSpend } from '../../Compounds/Advise/SideBySideView/EngagementEditorContext';
import { CurrentSuppliersQueue } from './useCurrentSuppliersQueue';

const OVERRIDE_FAILED = "Couldn't save the override. Please try again.";
const RESET_FAILED = "Couldn't reset the current spend. Please try again.";

type EngagementCall = (id: string) => Promise<CallResult<AdviseEngagement>>;

/**
 * Overrides, resets and re-reads the engagement's current spend in turn with the Current Suppliers saves,
 * which show the current spend each answer carries.
 */
export const useCurrentSpendOverride = ({
  engagementRef,
  canEdit,
  sendInTurn,
  showRefusal
}: CurrentSuppliersQueue): EngagementCurrentSpend => {
  const send = useCallback(
    async (call: EngagementCall, failed: string | null) => {
      const id = engagementRef.current?.id;
      if (!id) return false;
      const { error } = await sendInTurn(id, () => call(id));
      if (error && failed) showRefusal(refusalMessage(error, failed));
      return !error;
    },
    [engagementRef, sendInTurn, showRefusal]
  );

  const override = useCallback(
    async (monthlyAmount: number) =>
      canEdit && send((id) => updateAdviseCurrentSpendOverride(id, monthlyAmount), OVERRIDE_FAILED),
    [canEdit, send]
  );

  const reset = useCallback(
    async () => canEdit && send(deleteAdviseCurrentSpendOverride, RESET_FAILED),
    [canEdit, send]
  );

  const refresh = useCallback(async () => {
    await send(fetchAdviseEngagementById, null);
  }, [send]);

  return useMemo(() => ({ override, reset, refresh }), [override, reset, refresh]);
};
